var _a;
import config from '@/config';
import SignalRService from '@/shared/services/SignalRService';
import eventBus from 'pubsub-js';
import { getCurrentTime } from '@/shared/components/registration/registrationHelpers';
import api from '../api';
import SharedWorkerPolyfill from './SharedWorkerPolyfill';
import userStorage from './userStorage';
import AuthService, { functionalities, privileges } from './AuthService';
const lastRegStorageKey = 'LastTmrReg';
export default class TimerRegistrationService {
    // Timer worker methods
    static postMessageToWorker(message) {
        if (this.worker) {
            this.worker.postMessage(message);
        }
    }
    static stopTimer() {
        this.postMessageToWorker({ event: 'stop' });
    }
    static startTimer(registrationId) {
        if (AuthService.hasPermission({
            functionality: functionalities.RegistrationOwn,
            privilege: privileges.Create,
        }))
            this.postMessageToWorker({ event: 'start', registrationId });
    }
    static setRegistrationId(registrationId) {
        this.postMessageToWorker({ event: 'setRegistrationId', registrationId });
    }
    static async createTimerRegistration(value) {
        var _b, _c;
        if (!AuthService.hasPermission({
            functionality: functionalities.RegistrationOwn,
            privilege: privileges.Create,
        })) {
            return Promise.reject();
        }
        const { data } = await api.base.post(`${config.appUrls.api}/api/TimerRegistration/StartTimer`, value);
        const result = data;
        this.current = result;
        const registrationId = (_c = (_b = this.current) === null || _b === void 0 ? void 0 : _b.registration) === null || _c === void 0 ? void 0 : _c.id;
        if (result === null || result === void 0 ? void 0 : result.registration) {
            userStorage.setItem(lastRegStorageKey, value);
            this.startTimer(registrationId);
        }
        eventBus.publish('timerRegistrationChanged');
        return result;
    }
    static async restartTimerRegistration() {
        if (!AuthService.hasPermission({
            functionality: functionalities.RegistrationOwn,
            privilege: privileges.Create,
        })) {
            return Promise.reject();
        }
        const lastReg = userStorage.getItem(lastRegStorageKey);
        if (!lastReg)
            return Promise.resolve(null);
        lastReg.fromMinutes = getCurrentTime();
        return this.createTimerRegistration(lastReg);
    }
    static async stopTimerRegistration(pause = false) {
        var _b;
        if (!AuthService.hasPermission({
            functionality: functionalities.RegistrationOwn,
            privilege: privileges.Create,
        })) {
            return Promise.reject();
        }
        if (!pause) {
            userStorage.setItem(lastRegStorageKey, null);
        }
        this.stopTimer();
        if (!((_b = this.current) === null || _b === void 0 ? void 0 : _b.registration))
            return Promise.resolve(null);
        const { data } = await api.base.get(`${config.appUrls.api}/api/TimerRegistration/StopTimer/${this.current.registration.id}`, {
            params: { now: getCurrentTime() },
        });
        const result = data;
        this.current = null;
        eventBus.publish('timerRegistrationChanged');
        return result;
    }
    static async pauseTimerRegistration() {
        return this.stopTimerRegistration(true);
    }
}
_a = TimerRegistrationService;
TimerRegistrationService.worker = null;
TimerRegistrationService.current = null;
TimerRegistrationService.SharedWorkerSupported = 'SharedWorker' in globalThis;
TimerRegistrationService.init = () => {
    if (_a.SharedWorkerSupported) {
        _a.worker = new SharedWorkerPolyfill('/assets/scripts/timerSharedWorker.js', {
            name: 'TimerRegistrationWorker',
        });
        _a.worker.onmessage = _a.handleWorkerMessage;
        _a.worker.start();
    }
    SignalRService.subscribe('timerRegistrationUpdated', data => {
        var _b, _c;
        _a.current = data;
        const registrationId = (_c = (_b = _a.current) === null || _b === void 0 ? void 0 : _b.registration) === null || _c === void 0 ? void 0 : _c.id;
        _a.setRegistrationId(registrationId);
        eventBus.publish('timerRegistrationChanged');
    });
    if (AuthService.hasPermission({
        functionality: functionalities.RegistrationOwn,
        privilege: privileges.Create,
    })) {
        api.base.get(`${config.appUrls.api}/api/TimerRegistration`).then(({ data }) => {
            var _b, _c;
            _a.current = data;
            const registrationId = (_c = (_b = _a.current) === null || _b === void 0 ? void 0 : _b.registration) === null || _c === void 0 ? void 0 : _c.id;
            _a.setRegistrationId(registrationId);
            eventBus.publish('timerRegistrationChanged');
        });
    }
    window.addEventListener('unload', () => {
        if (_a.worker) {
            _a.worker.postMessage({ event: 'disconnect' });
            _a.worker.close();
        }
    });
};
TimerRegistrationService.handleWorkerMessage = ({ data }) => {
    const message = data;
    if (!message)
        return;
    switch (message.event) {
        case 'tick': {
            _a.doTimerTick(message.registrationId);
            break;
        }
        default:
            break;
    }
};
TimerRegistrationService.doTimerTick = (registrationId) => {
    if (!AuthService.hasPermission({
        functionality: functionalities.RegistrationOwn,
        privilege: privileges.Update,
    })) {
        return;
    }
    api.base
        .get(`${config.appUrls.api}/api/TimerRegistration/UpdateTimer/${registrationId}`, {
        params: { now: getCurrentTime() },
    })
        .then(({ data }) => {
        var _b;
        _a.current = data;
        if (!((_b = _a.current) === null || _b === void 0 ? void 0 : _b.registration)) {
            _a.stopTimer();
            return;
        }
        // A timer that runs past midnight is continued in a registration on the new day, so keep the
        // worker pointed at whatever registration the server reports as running.
        if (_a.current.registration.id !== registrationId) {
            _a.setRegistrationId(_a.current.registration.id);
        }
    }, error => {
        _a.current = null;
        _a.stopTimer();
        console.error(error);
    });
};
TimerRegistrationService.isRunning = () => {
    if (!_a.current)
        return false;
    return _a.current.isRunning;
};
TimerRegistrationService.getRunningRegistration = () => {
    if (!_a.current || !_a.current.isRunning)
        return null;
    return _a.current.registration;
};
TimerRegistrationService.isPaused = () => {
    if (_a.isRunning())
        return false;
    return !!userStorage.getItem(lastRegStorageKey);
};
